/**
 * tRPC Provider
 */
import { useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { httpBatchLink } from '@trpc/client'
import { trpc } from '@/lib/trpc'
import { getStoredTokens, refreshTokens, saveTokens, isTokenExpired } from '@/lib/cognito-auth'

const API_BASE = import.meta.env.VITE_API_URL

async function getAccessToken(): Promise<string | null> {
  const stored = getStoredTokens()
  if (!stored) return null

  // Refresh if expired (with 60s buffer)
  if (isTokenExpired(stored.expiresAt - 60000)) {
    try {
      const newTokens = await refreshTokens(stored.refreshToken)
      saveTokens({
        ...newTokens,
        refreshToken: stored.refreshToken,
      })
      return newTokens.accessToken
    } catch {
      return null
    }
  }

  return stored.accessToken
}

interface TRPCProviderProps {
  children: React.ReactNode
}

export function TRPCProvider({ children }: TRPCProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30 * 1000,
            retry: 3,
            refetchOnWindowFocus: true,
          },
          mutations: {
            retry: 1,
          },
        },
      })
  )

  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: `${API_BASE}/trpc`,
          async headers() {
            const token = await getAccessToken()
            return token ? { Authorization: `Bearer ${token}` } : {}
          },
        }),
      ],
    })
  )

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  )
}
